import { useState, useEffect } from "react";

/**
 * useIsCompact — the ONE responsive boundary for the app (DSP mobile re-design).
 *
 * Returns true on phone-width viewports (<= 640px), false above. This is the
 * single source of truth for the compact/roomy split; components branch their
 * inline style objects on it. There is deliberately no second breakpoint tier —
 * the DSP audience is ~95% phone, so one boolean keeps every surface to two
 * layouts, not four combinations. If a genuine tablet need appears, revisit here
 * (one place), don't scatter media queries.
 *
 * Phone-first default: if window/matchMedia is unavailable (SSR, tests), assume
 * compact — the phone layout is the primary one.
 *
 * The 640px boundary is mirrored in a comment anywhere CSS needs it; this hook
 * owns the value.
 */
const COMPACT_QUERY = "(max-width: 640px)";

export function useIsCompact() {
  const [isCompact, setIsCompact] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true; // phone-first
    return window.matchMedia(COMPACT_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(COMPACT_QUERY);
    const onChange = (e) => setIsCompact(e.matches);
    // addEventListener is the modern API; older Safari used addListener.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    // Sync once in case it changed between initial state and effect.
    setIsCompact(mq.matches);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return isCompact;
}
